import type { Sensor } from '../../services/sensors';

function genHistory(base: number, amplitude: number, points = 24): number[] {
  return Array.from({ length: points }, (_, i) => {
    const wave = Math.sin((i / points) * Math.PI * 2) * amplitude;
    const noise = (Math.random() - 0.5) * amplitude * 0.3;
    return Math.round((base + wave + noise) * 10) / 10;
  });
}

// ── Parameter configuration (shared across components) ───────
// depthThresholds[i] maps to DEPTHS[i]; used for per-depth status coloring.
// Designed to be replaced per-tenant via a future Settings UI.
export const PARAM_CONFIG = [
  {
    key: 'humidity', label: 'Humidity', unit: '%', lo: 15, hi: 50, icon: '💧',
    shallowRange: '20–40%', deepRange: '15–35%',
    depthThresholds: [
      { lo: 20, hi: 40 },
      { lo: 25, hi: 50 },
      { lo: 25, hi: 50 },
      { lo: 20, hi: 45 },
      { lo: 15, hi: 35 },
      { lo: 15, hi: 35 },
      { lo: 15, hi: 35 },
    ],
  },
  {
    key: 'temp', label: 'Temperature', unit: '°C', lo: 12, hi: 25, icon: '🌡',
    shallowRange: '15–22°C', deepRange: '12–20°C',
    depthThresholds: [
      { lo: 15, hi: 22 },
      { lo: 15, hi: 22 },
      { lo: 15, hi: 22 },
      { lo: 12, hi: 20 },
      { lo: 12, hi: 20 },
      { lo: 12, hi: 20 },
      { lo: 12, hi: 20 },
    ],
  },
  {
    key: 'conductivity', label: 'Conductivity', unit: 'µS/cm', lo: 500, hi: 1500, icon: '⚡',
    shallowRange: '500–1200 µS/cm', deepRange: '500–1000 µS/cm',
    depthThresholds: [
      { lo: 500, hi: 1200 },
      { lo: 500, hi: 1200 },
      { lo: 500, hi: 1200 },
      { lo: 500, hi: 1000 },
      { lo: 500, hi: 1000 },
      { lo: 500, hi: 1000 },
      { lo: 500, hi: 1000 },
    ],
  },
  // Salinidad: QUITADA el 2026-09-01. No es una medición aparte — YaTun
  // confirmó por escrito que salinidad y conductividad "son el mismo valor
  // porque son la misma magnitud física, sólo hay que conservar el EC del
  // suelo". Coincide con lo que ya veíamos: el equipo reportaba el MISMO
  // número en los dos registros, en las siete profundidades. Dos tarjetas
  // con el número idéntico y unidades distintas se leen como dos mediciones
  // que se confirman entre sí, y es una sola repetida.
  //
  // El dato se sigue guardando en telemetry (el decoder no cambió); si algún
  // día hace falta, basta con devolver esta entrada y el mapeo de
  // soil_salinity en METRIC_KEY_MAP.
  {
    key: 'nitrogen', label: 'Nitrogen', unit: 'mg/kg', lo: 5, hi: 25, icon: 'N',
    shallowRange: '10–25 mg/kg', deepRange: '5–15 mg/kg',
    depthThresholds: [
      { lo: 10, hi: 25 },
      { lo: 10, hi: 25 },
      { lo: 10, hi: 25 },
      { lo: 5,  hi: 15 },
      { lo: 5,  hi: 15 },
      { lo: 5,  hi: 15 },
      { lo: 5,  hi: 15 },
    ],
  },
  {
    key: 'phosphorus', label: 'Phosphorus', unit: 'mg/kg', lo: 15, hi: 40, icon: 'P',
    shallowRange: '15–40 mg/kg', deepRange: '15–40 mg/kg',
    depthThresholds: [
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
      { lo: 15, hi: 40 },
    ],
  },
  {
    key: 'potassium', label: 'Potassium', unit: 'mg/kg', lo: 80, hi: 200, icon: 'K',
    shallowRange: '100–200 mg/kg', deepRange: '80–150 mg/kg',
    depthThresholds: [
      { lo: 100, hi: 200 },
      { lo: 100, hi: 200 },
      { lo: 100, hi: 200 },
      { lo: 80,  hi: 150 },
      { lo: 80,  hi: 150 },
      { lo: 80,  hi: 150 },
      { lo: 80,  hi: 150 },
    ],
  },

  // Las métricas de Xiaoma (humedad, temperatura, EC, N/P/K, salinidad) NO
  // llevan tarjeta propia: se mapean a las claves de arriba en METRIC_KEY_MAP
  // (apps/core/src/modules/devices/sensor-view.ts), porque son la misma
  // medición física en la misma unidad. Un intento anterior las tuvo separadas
  // con sufijo "(Xiaoma)" y la lista quedó con todo duplicado — revertido por
  // DIZ el 2026-08-19: el fabricante del sensor no es una dimensión del dato
  // para el usuario final.
  //
  // NO agregar pH ni calcio acá. Estuvieron un tiempo (salían del Apéndice del
  // protocolo Xiaoma, que lista TODOS los tipos de sensor que el formato de
  // trama admite) pero el hardware del piloto no los mide y tampoco aparecen
  // en el portal del propio fabricante — así que las tarjetas se llenaban con
  // un cero inventado. Quitadas por DIZ el 2026-08-25.
  //
  // Respuesta de YaTun (2026-08-25), distinta para cada uno:
  //   · pH — SÍ es posible, con una sonda de pH adicional, pero DIZ lo archivó
  //     el 2026-08-25: viable y útil a futuro, no esencial hoy. Si algún día se
  //     compra, el decoder ya sabe leer el tipo 0x05 (xiaoma-sensor-types.ts):
  //     basta volver a agregar la entrada acá, su clave en METRIC_KEY_MAP y su
  //     traducción en soil.params — más confirmar con el fabricante en qué
  //     dirección de sensor reporta, porque de eso depende el portMap.
  //   · Calcio — NO existe. No fabrican sensor de calcio para suelo, así que
  //     no va a llegar por esta vía. El 0x34 del Apéndice queda como un tipo
  //     que el protocolo admite pero que ningún equipo suyo produce.
] as const;

export type ParamKey = typeof PARAM_CONFIG[number]['key'];
// Profundidades REALES del hardware (Yantai 7-layer capacitive).
// Fuente de verdad: packages/db/src/decoders/yantai-soil-7layer.ts (DEPTHS_CM).
// El hardware manda: si cambia el decoder, cambia esta lista — nunca al revés.
export const DEPTHS = [5, 10, 20, 30, 40, 60, 100];

/**
 * Profundidades REALES de un sensor, deducidas de sus propias métricas.
 *
 * DEPTHS de arriba son las del hardware Yantai (5/10/20/30/40/60/100 cm) y
 * estaban cableadas en toda la UI de suelo. Un sensor Xiaoma mide donde diga
 * su `portMap` — el del piloto de Leyer va de 10 a 70 cm cada 10 — así que
 * con la lista fija sus lecturas de 50 y 70 cm no se dibujaban en ningún
 * lado, y 5 y 100 salían siempre vacías. El eje de profundidad tiene que
 * seguir al sensor, no al revés.
 *
 * Las claves llegan como `<param>_<profundidad>` desde sensor-view.ts.
 * Sin métricas (demo o device sin reportar) se cae a DEPTHS.
 */
export function depthsFromMetrics(metrics: Record<string, number> | undefined | null): number[] {
  if (!metrics) return DEPTHS;
  const found = new Set<number>();
  for (const key of Object.keys(metrics)) {
    const m = key.match(/_(\d+)$/);
    if (m) found.add(Number(m[1]));
  }
  return found.size > 0 ? [...found].sort((a, b) => a - b) : DEPTHS;
}

// Returns per-depth threshold for a param, falling back to aggregate lo/hi.
// Components use this so the future tenant Settings UI is a drop-in replacement.
export function getDepthThreshold(
  paramCfg: typeof PARAM_CONFIG[number],
  depthIndex: number,
): { lo: number; hi: number } {
  const dt = (paramCfg as { depthThresholds?: ReadonlyArray<{ lo: number; hi: number }> }).depthThresholds;
  return dt?.[depthIndex] ?? { lo: paramCfg.lo, hi: paramCfg.hi };
}

// ── Soil sensors — Georgia Pecan Farms, Warner Robins, GA ────
export const DEMO_SENSORS: Sensor[] = [
  {
    id: 'demo-1', imei: 'DEMO000001', name: 'Sector Norte A',
    lat: 32.5820, lng: -83.8690, status: 'good', type: 'soil' as const, battery: 89, solarCharge: 48, signal: 94, firmware: '2.4.1',
    lastReading: {
      receivedAt: new Date().toISOString(),
      metrics: {
        humidity_5: 32.4, humidity_10: 38.6, humidity_20: 42.1, humidity_30: 35.8, humidity_40: 27.4, humidity_60: 24.2, humidity_100: 21.8,
        temp_5: 18.5,     temp_10: 17.2,     temp_20: 16.1,     temp_30: 14.8,     temp_40: 13.9,     temp_60: 13.2,     temp_100: 12.6,
        conductivity_5: 720,  conductivity_10: 810,  conductivity_20: 940,  conductivity_30: 780,  conductivity_40: 680,  conductivity_60: 650,  conductivity_100: 610,
        salinity_5: 420,  salinity_10: 480,  salinity_20: 540,  salinity_30: 480,  salinity_40: 440,  salinity_60: 410,  salinity_100: 380,
        nitrogen_5: 18.2, nitrogen_10: 15.6, nitrogen_20: 13.4, nitrogen_30: 11.8, nitrogen_40: 9.2,  nitrogen_60: 7.8,  nitrogen_100: 6.4,
        phosphorus_5: 28.4, phosphorus_10: 25.1, phosphorus_20: 22.3, phosphorus_30: 19.8, phosphorus_40: 17.5, phosphorus_60: 16.2, phosphorus_100: 15.8,
        potassium_5: 148,  potassium_10: 138,  potassium_20: 128,  potassium_30: 118,  potassium_40: 105,  potassium_60: 95,   potassium_100: 88,
      },
    },
  },
  {
    id: 'demo-2', imei: 'DEMO000002', name: 'Sector Sur B',
    lat: 32.5695, lng: -83.8655, status: 'warn', type: 'soil' as const, battery: 61, solarCharge: 32, signal: 78, firmware: '2.4.1',
    lastReading: {
      receivedAt: new Date().toISOString(),
      metrics: {
        humidity_5: 17.5, humidity_10: 22.4, humidity_20: 27.2, humidity_30: 19.5, humidity_40: 13.1, humidity_60: 11.8, humidity_100: 10.2,
        temp_5: 20.8,     temp_10: 19.1,     temp_20: 17.5,     temp_30: 15.2,     temp_40: 13.8,     temp_60: 13.0,     temp_100: 12.4,
        conductivity_5: 1180, conductivity_10: 1050, conductivity_20: 1280, conductivity_30: 920,  conductivity_40: 780,  conductivity_60: 740,  conductivity_100: 680,
        salinity_5: 820,  salinity_10: 780,  salinity_20: 840,  salinity_30: 720,  salinity_40: 680,  salinity_60: 640,  salinity_100: 610,
        nitrogen_5: 9.2,  nitrogen_10: 8.1,  nitrogen_20: 7.4,  nitrogen_30: 6.8,  nitrogen_40: 6.2,  nitrogen_60: 4.8,  nitrogen_100: 4.2,
        phosphorus_5: 24.1, phosphorus_10: 21.8, phosphorus_20: 18.4, phosphorus_30: 17.2, phosphorus_40: 16.1, phosphorus_60: 14.8, phosphorus_100: 13.2,
        potassium_5: 128,  potassium_10: 115,  potassium_20: 108,  potassium_30: 92,   potassium_40: 84,   potassium_60: 78,   potassium_100: 72,
      },
    },
  },
  {
    id: 'demo-3', imei: 'DEMO000003', name: 'Sector Este C',
    lat: 32.5765, lng: -83.8580, status: 'good', type: 'soil' as const, battery: 76, solarCharge: 41, signal: 88, firmware: '2.3.9',
    lastReading: {
      receivedAt: new Date().toISOString(),
      metrics: {
        humidity_5: 35.2, humidity_10: 42.1, humidity_20: 44.8, humidity_30: 38.5, humidity_40: 29.2, humidity_60: 25.8, humidity_100: 22.4,
        temp_5: 17.8,     temp_10: 16.5,     temp_20: 15.2,     temp_30: 13.8,     temp_40: 13.2,     temp_60: 12.8,     temp_100: 12.2,
        conductivity_5: 680,  conductivity_10: 740,  conductivity_20: 870,  conductivity_30: 720,  conductivity_40: 640,  conductivity_60: 610,  conductivity_100: 580,
        salinity_5: 390,  salinity_10: 430,  salinity_20: 495,  salinity_30: 445,  salinity_40: 408,  salinity_60: 378,  salinity_100: 352,
        nitrogen_5: 16.8, nitrogen_10: 14.2, nitrogen_20: 12.1, nitrogen_30: 10.4, nitrogen_40: 8.5,  nitrogen_60: 7.2,  nitrogen_100: 6.1,
        phosphorus_5: 27.5, phosphorus_10: 24.2, phosphorus_20: 21.5, phosphorus_30: 19.2, phosphorus_40: 17.8, phosphorus_60: 16.5, phosphorus_100: 15.9,
        potassium_5: 152,  potassium_10: 142,  potassium_20: 132,  potassium_30: 122,  potassium_40: 108,  potassium_60: 98,   potassium_100: 91,
      },
    },
  },
  {
    id: 'demo-4', imei: 'DEMO000004', name: 'Sector Oeste D',
    lat: 32.5748, lng: -83.8775, status: 'alert', type: 'soil' as const, battery: 18, solarCharge: 7, signal: 42, firmware: '2.3.9',
    lastReading: {
      receivedAt: new Date().toISOString(),
      metrics: {
        humidity_5: 10.2, humidity_10: 14.8, humidity_20: 18.2, humidity_30: 12.5, humidity_40: 8.4,  humidity_60: 6.8,  humidity_100: 5.9,
        temp_5: 24.2,     temp_10: 21.8,     temp_20: 19.4,     temp_30: 17.2,     temp_40: 15.1,     temp_60: 13.8,     temp_100: 12.9,
        conductivity_5: 1450, conductivity_10: 1320, conductivity_20: 1580, conductivity_30: 1180, conductivity_40: 920,  conductivity_60: 1050, conductivity_100: 880,
        salinity_5: 1120, salinity_10: 980,  salinity_20: 1060, salinity_30: 880,  salinity_40: 780,  salinity_60: 720,  salinity_100: 680,
        nitrogen_5: 4.8,  nitrogen_10: 4.2,  nitrogen_20: 3.8,  nitrogen_30: 3.4,  nitrogen_40: 3.1,  nitrogen_60: 2.8,  nitrogen_100: 2.5,
        phosphorus_5: 13.2, phosphorus_10: 11.5, phosphorus_20: 9.8,  phosphorus_30: 8.4,  phosphorus_40: 7.2,  phosphorus_60: 6.5,  phosphorus_100: 5.9,
        potassium_5: 72,   potassium_10: 65,   potassium_20: 58,   potassium_30: 52,   potassium_40: 47,   potassium_60: 43,   potassium_100: 39,
      },
    },
  },
];

export function getDemoSensorHistory(imei: string): Array<{ receivedAt: string; metrics: Record<string, number> }> {
  const now = Date.now();
  const sensor = DEMO_SENSORS.find(s => s.imei === imei);
  const base = sensor?.lastReading?.metrics ?? {};

  return Array.from({ length: 24 }, (_, i) => ({
    receivedAt: new Date(now - (23 - i) * 3600_000).toISOString(),
    metrics: {
      humidity_5: genHistory(base.humidity_5 ?? 35, 5, 24)[i],
      humidity_10: genHistory(base.humidity_10 ?? 38, 4, 24)[i],
      humidity_20: genHistory(base.humidity_20 ?? 40, 3, 24)[i],
      humidity_30: genHistory(base.humidity_30 ?? 42, 2, 24)[i],
      temp_5: genHistory(base.temp_5 ?? 18, 3, 24)[i],
      temp_10: genHistory(base.temp_10 ?? 17, 2, 24)[i],
    },
  }));
}

// Generates historical data for a specific parameter, averaged across all
// depths, across an explicit [startMs, endMs] window — the caller (the
// Historical tab's day/week/month/year picker) resolves that window from
// whatever calendar period the user picked, not just "ending now".
//
// ~1 in 5 points gets an extra wobble on top of the smooth seasonal curve,
// so the series reads like real (imperfect) sensor data instead of a clean
// synthetic wave — real soil sensors drift and have noisy readings.
export function getDemoParamHistory(
  imei: string,
  paramKey: ParamKey,
  range: 'day' | 'week' | 'month' | 'year',
  startMs: number,
  endMs: number,
): Array<{ ts: string; value: number }> {
  const sensor = DEMO_SENSORS.find(s => s.imei === imei);
  const metrics = sensor?.lastReading?.metrics ?? {};
  const depthValues = DEPTHS.map(d => metrics[`${paramKey}_${d}`] ?? 0);
  const avg = depthValues.reduce((a, b) => a + b, 0) / depthValues.length;

  const pointsByRange: Record<string, number> = { day: 24, week: 28, month: 30, year: 12 };
  const points = Math.max(pointsByRange[range] ?? 24, 2);
  const stepMs = (endMs - startMs) / Math.max(points - 1, 1);

  return Array.from({ length: points }, (_, i) => {
    const tsMs = startMs + i * stepMs;
    const seasonal = Math.sin((i / points) * Math.PI * 2) * avg * 0.12;
    const isNoisy = Math.random() < 0.2;
    const noiseAmp = isNoisy ? avg * (0.12 + Math.random() * 0.15) : avg * 0.04;
    const noise = (Math.random() - 0.5) * 2 * noiseAmp;
    return {
      ts: new Date(tsMs).toISOString(),
      value: Math.round(Math.max(0, avg + seasonal + noise) * 10) / 10,
    };
  });
}

export function getDemoBatteryHistory(
  imei: string,
  range: 'day' | 'week' | 'month' = 'day',
): Array<{ ts: string; battery: number; solar: number }> {
  const sensor = DEMO_SENSORS.find(s => s.imei === imei);
  const currentBattery = sensor?.battery ?? 50;
  const maxSolar = (sensor as any)?.solarCharge ?? 30;

  const now = Date.now();
  const configs = {
    day:   { points: 24, stepMs: 3_600_000 },
    week:  { points: 28, stepMs: 6 * 3_600_000 },
    month: { points: 30, stepMs: 24 * 3_600_000 },
  };
  const { points, stepMs } = configs[range];

  return Array.from({ length: points }, (_, i) => {
    const tsMs = now - (points - 1 - i) * stepMs;
    const hour = new Date(tsMs).getHours();
    const isDaytime = hour >= 6 && hour <= 19;
    const solarFraction = isDaytime
      ? Math.max(0, Math.sin(((hour - 6) / 13) * Math.PI))
      : 0;
    const solar = Math.round(maxSolar * solarFraction * (0.85 + Math.random() * 0.3) * 10) / 10;
    const drift = Math.sin((i / points) * Math.PI * 2) * 8 + (Math.random() - 0.5) * 3;
    const battery = Math.min(100, Math.max(1, Math.round((currentBattery + drift) * 10) / 10));
    return { ts: new Date(tsMs).toISOString(), battery, solar };
  });
}

// ── Weather station configuration ────────────────────────────
export const WEATHER_PARAM_CONFIG = [
  { key: 'temperature',   label: 'Temperature',        unit: '°C',   lo: 10,  hi: 38,   icon: '🌡' },
  { key: 'humidity',      label: 'Humidity',            unit: '%',    lo: 20,  hi: 85,   icon: '💧' },
  { key: 'windSpeed',     label: 'Wind Speed',          unit: 'km/h', lo: 0,   hi: 50,   icon: '💨' },
  { key: 'radiation',     label: 'Solar Radiation',     unit: 'W/m²', lo: 0,   hi: 1200, icon: '☀️' },
  { key: 'rain',          label: 'Precipitation',       unit: 'mm',   lo: 0,   hi: 20,   icon: '🌧' },
  { key: 'pressure',      label: 'Barometric Pressure', unit: 'hPa',  lo: 970, hi: 1040, icon: '🔵' },
  { key: 'dewPoint',      label: 'Dew Point',           unit: '°C',   lo: 5,   hi: 25,   icon: '❄️' },
  { key: 'uvIndex',       label: 'UV Index',            unit: 'UV',   lo: 0,   hi: 8,    icon: '🔆' },
] as const;

export type WeatherParamKey = typeof WEATHER_PARAM_CONFIG[number]['key'];

export interface WeatherStation {
  id: string;
  imei: string;
  name: string;
  lat: number;
  lng: number;
  status: 'good' | 'warn' | 'alert';
  // Campos opcionales (salvo windDirLabel): las estaciones demo siempre
  // traen las 9, pero una estación real (useWeatherStations.ts) puede
  // faltarle cualquiera — no todo hardware reporta lluvia/UV/etc., y
  // dewPoint/windDirLabel no tienen equivalente en el decoder de Xiaoma
  // hoy. Quien consuma esto debe tratar un valor ausente como "sin dato",
  // nunca asumir 0 (ver WeatherPage.tsx `averages`).
  current: {
    temperature?: number; humidity?: number; windSpeed?: number; windDirLabel: string;
    radiation?: number; rain?: number; pressure?: number; dewPoint?: number; uvIndex?: number;
  };
  forecast: Array<{ label: string; icon: string; temp: number; rain: number }>;
}

function weatherParamValue(s: WeatherStation, key: string): number {
  return (s.current as unknown as Record<string, number>)[key] ?? 0;
}

export const DEMO_WEATHER_STATIONS: WeatherStation[] = [
  {
    id: 'ws-1', imei: 'WS000001', name: 'Norte Station',
    lat: 32.5828, lng: -83.8692, status: 'good',
    current: { temperature: 28.4, humidity: 52, windSpeed: 14.2, windDirLabel: 'SW', radiation: 820, rain: 0.0, pressure: 1009, dewPoint: 17.8, uvIndex: 7 },
    forecast: [
      { label: '+4h',  icon: '☀️',  temp: 30, rain: 5  }, { label: '+8h',  icon: '🌤', temp: 29, rain: 8  },
      { label: '+12h', icon: '⛅', temp: 27, rain: 15 }, { label: '+16h', icon: '🌦', temp: 25, rain: 40 },
      { label: '+20h', icon: '🌧', temp: 23, rain: 65 }, { label: '+24h', icon: '⛅', temp: 24, rain: 20 },
      { label: '+28h', icon: '🌤', temp: 27, rain: 10 }, { label: '+32h', icon: '☀️',  temp: 31, rain: 5  },
    ],
  },
  {
    id: 'ws-2', imei: 'WS000002', name: 'Sur Station',
    lat: 32.5693, lng: -83.8652, status: 'warn',
    current: { temperature: 33.1, humidity: 38, windSpeed: 22.4, windDirLabel: 'S', radiation: 960, rain: 0.0, pressure: 1006, dewPoint: 15.2, uvIndex: 10 },
    forecast: [
      { label: '+4h',  icon: '☀️',  temp: 34, rain: 2  }, { label: '+8h',  icon: '☀️',  temp: 32, rain: 5  },
      { label: '+12h', icon: '🌤', temp: 30, rain: 12 }, { label: '+16h', icon: '⛅', temp: 28, rain: 30 },
      { label: '+20h', icon: '🌦', temp: 26, rain: 55 }, { label: '+24h', icon: '🌧', temp: 25, rain: 70 },
      { label: '+28h', icon: '⛅', temp: 27, rain: 25 }, { label: '+32h', icon: '🌤', temp: 29, rain: 8  },
    ],
  },
  {
    id: 'ws-3', imei: 'WS000003', name: 'Este Station',
    lat: 32.5768, lng: -83.8577, status: 'good',
    current: { temperature: 29.8, humidity: 49, windSpeed: 11.3, windDirLabel: 'SW', radiation: 870, rain: 0.0, pressure: 1008, dewPoint: 17.5, uvIndex: 8 },
    forecast: [
      { label: '+4h',  icon: '☀️',  temp: 31, rain: 4  }, { label: '+8h',  icon: '🌤', temp: 30, rain: 7  },
      { label: '+12h', icon: '⛅', temp: 28, rain: 18 }, { label: '+16h', icon: '🌦', temp: 26, rain: 45 },
      { label: '+20h', icon: '🌧', temp: 24, rain: 60 }, { label: '+24h', icon: '⛅', temp: 25, rain: 18 },
      { label: '+28h', icon: '🌤', temp: 28, rain: 8  }, { label: '+32h', icon: '☀️',  temp: 30, rain: 4  },
    ],
  },
];

export function getDemoWeatherParamHistory(
  imei: string,
  paramKey: string,
  range: 'day' | 'week' | 'month' = 'day',
): Array<{ ts: string; value: number }> {
  const station = DEMO_WEATHER_STATIONS.find(s => s.imei === imei);
  const base = station ? weatherParamValue(station, paramKey) : 20;
  const configs = {
    day:   { points: 24, stepMs: 3_600_000 },
    week:  { points: 28, stepMs: 6 * 3_600_000 },
    month: { points: 30, stepMs: 24 * 3_600_000 },
  };
  const { points, stepMs } = configs[range];
  const now = Date.now();

  return Array.from({ length: points }, (_, i) => {
    const tsMs = now - (points - 1 - i) * stepMs;
    const hour = new Date(tsMs).getHours();
    let value = base;
    if (paramKey === 'temperature') {
      value = base + Math.sin(((hour - 3 + 24) % 24 / 24) * Math.PI) * 5;
    } else if (paramKey === 'humidity') {
      value = base - Math.sin(((hour - 3 + 24) % 24 / 24) * Math.PI) * 10;
    } else if (paramKey === 'radiation' || paramKey === 'uvIndex') {
      const isDaytime = hour >= 6 && hour <= 19;
      value = isDaytime ? base * Math.max(0, Math.sin(((hour - 6) / 13) * Math.PI)) : 0;
    } else {
      value = base + Math.sin((i / points) * Math.PI * 2) * base * 0.08;
    }
    value = Math.max(0, value + (Math.random() - 0.5) * base * 0.06);
    return { ts: new Date(tsMs).toISOString(), value: Math.round(value * 10) / 10 };
  });
}

export function getDemoWeatherParamHistoryRange(
  imei: string,
  paramKey: string,
  startMs: number,
  endMs: number,
): Array<{ ts: string; value: number }> {
  const station = DEMO_WEATHER_STATIONS.find(s => s.imei === imei);
  const base = station ? (station.current as unknown as Record<string, number>)[paramKey] ?? 20 : 20;
  const durationMs = Math.max(endMs - startMs, 3_600_000);
  const points = Math.min(Math.round(durationMs / 3_600_000), 500);
  const stepMs = durationMs / Math.max(points - 1, 1);

  return Array.from({ length: points }, (_, i) => {
    const tsMs = startMs + i * stepMs;
    const hour = new Date(tsMs).getHours();
    let value = base;
    if (paramKey === 'temperature') {
      value = base + Math.sin(((hour - 3 + 24) % 24 / 24) * Math.PI) * 5;
    } else if (paramKey === 'humidity') {
      value = base - Math.sin(((hour - 3 + 24) % 24 / 24) * Math.PI) * 10;
    } else if (paramKey === 'radiation' || paramKey === 'uvIndex') {
      const isDaytime = hour >= 6 && hour <= 19;
      value = isDaytime ? base * Math.max(0, Math.sin(((hour - 6) / 13) * Math.PI)) : 0;
    } else {
      value = base + Math.sin((i / points) * Math.PI * 2) * base * 0.08;
    }
    value = Math.max(0, value + (Math.random() - 0.5) * base * 0.06);
    return { ts: new Date(tsMs).toISOString(), value: Math.round(value * 10) / 10 };
  });
}

// ── Weather data (legacy single-station) ─────────────────────
export const DEMO_WEATHER = {
  current: {
    temp: 28.4, humidity: 52, windSpeed: 14.2, windDir: 225,
    windDirLabel: 'SW', radiation: 820, rain: 0.0, pressure: 1009, dewPoint: 17.8, uvIndex: 7,
  },
  metrics: [
    { name: 'temperature',        value: 28.4,  unit: '°C',   lo: 10, hi: 38,   history: genHistory(28, 4) },
    { name: 'relativeHumidity',   value: 52,    unit: '%',    lo: 20, hi: 85,   history: genHistory(52, 10) },
    { name: 'windSpeed',          value: 14.2,  unit: 'km/h', lo: 0,  hi: 50,   history: genHistory(14, 6) },
    { name: 'solarRadiation',     value: 820,   unit: 'W/m²', lo: 0,  hi: 1200, history: genHistory(500, 300) },
    { name: 'precipitation',      value: 0.0,   unit: 'mm',   lo: 0,  hi: 20,   history: genHistory(0.5, 1) },
    { name: 'barometricPressure', value: 1009,  unit: 'hPa',  lo: 970, hi: 1040, history: genHistory(1009, 3) },
    { name: 'dewPoint',           value: 17.8,  unit: '°C',   lo: 5,  hi: 25,   history: genHistory(17, 2) },
    { name: 'uvIndex',            value: 7,     unit: 'UV',   lo: 0,  hi: 11,   history: genHistory(5, 3) },
  ],
  forecast: [
    { label: '+4h',  icon: '☀️',  temp: 30, rain: 5  },
    { label: '+8h',  icon: '🌤', temp: 29, rain: 8  },
    { label: '+12h', icon: '⛅', temp: 27, rain: 15 },
    { label: '+16h', icon: '🌦', temp: 25, rain: 40 },
    { label: '+20h', icon: '🌧', temp: 23, rain: 65 },
    { label: '+24h', icon: '⛅', temp: 24, rain: 20 },
    { label: '+28h', icon: '🌤', temp: 27, rain: 10 },
    { label: '+32h', icon: '☀️',  temp: 31, rain: 5  },
  ],
};

// ── Pest monitoring ──────────────────────────────────────────
export const DEMO_PESTS = {
  zones: [
    { id: 'z1', risk: 'high' as const,   lat: 32.5820, lng: -83.8690, pests: ['Whitefly', 'Spider Mite'], lastInspection: '1day',  trend: 'up' as const },
    { id: 'z2', risk: 'medium' as const, lat: 32.5756, lng: -83.8675, pests: ['Aphid'],                   lastInspection: '2days', trend: 'stable' as const },
    { id: 'z3', risk: 'low' as const,    lat: 32.5695, lng: -83.8655, pests: [],                          lastInspection: '3days', trend: 'down' as const },
    { id: 'z4', risk: 'medium' as const, lat: 32.5765, lng: -83.8580, pests: ['Thrips'],                  lastInspection: '1day',  trend: 'stable' as const },
  ],
  recentAlerts: [
    { id: 'a1', zoneId: 'z1', pest: 'Whitefly',    severity: 'high' as const,   timeKey: '3h' },
    { id: 'a2', zoneId: 'z1', pest: 'Spider Mite', severity: 'high' as const,   timeKey: '5h' },
    { id: 'a3', zoneId: 'z2', pest: 'Aphid',       severity: 'medium' as const, timeKey: '1day' },
    { id: 'a4', zoneId: 'z4', pest: 'Thrips',      severity: 'medium' as const, timeKey: '2days' },
    { id: 'a5', zoneId: 'z3', pest: 'No detection',severity: 'low' as const,    timeKey: '3days' },
  ],
  history: {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    Whitefly:      [2, 3, 4, 6, 8, 7, 9],
    'Spider Mite': [0, 1, 2, 2, 3, 4, 5],
    Aphid:         [1, 1, 2, 2, 2, 3, 3],
    Thrips:        [0, 0, 1, 1, 2, 2, 2],
  },
};
